<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { renderTraceLogAsText } from "../features/dashboard/dashboardTraceMapper.ts";
import { fetchTraceLog } from "../services/apiClient.ts";
import { useRedistributionStore } from "../stores/redistributionStore.ts";

const route = useRoute();
const router = useRouter();
const redistributionStore = useRedistributionStore();
const { traceLog, redistributionResult } = storeToRefs(redistributionStore);

const traceContent = ref<string | undefined>(undefined);
const error = ref<string | undefined>(undefined);
const loading = ref(true);

const queryBlobId = Array.isArray(route.query.blobId)
  ? route.query.blobId[0]
  : (route.query.blobId as string | undefined);

const effectiveBlobId = computed(
  () => queryBlobId ?? redistributionResult.value?.traceLogBlobId
);

const displayContent = computed(() => {
  if (!traceContent.value) return "";
  return renderTraceLogAsText(traceContent.value);
});

const successfulMovesCount = computed((): number | undefined => {
  if (!traceContent.value) return 0;
  if (traceContent.value.trimStart().startsWith("{")) {
    try {
      const json = JSON.parse(traceContent.value) as Record<string, unknown>;
      if ("successfulMoves" in json) return Number(json.successfulMoves);
    } catch {
      // fall through
    }
  }
  return undefined;
});

const traceLines = computed(() =>
  displayContent.value
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
);

const errorLineCount = computed(
  () => traceLines.value.filter((line) => /\berror\b/i.test(line)).length
);
const warningLineCount = computed(
  () => traceLines.value.filter((line) => /\bwarn(ing)?\b/i.test(line)).length
);
const keyEvents = computed(() =>
  traceLines.value
    .filter((line) =>
      /\b(start|finish|complete|movement|error|warn|retry|timeout)\b/i.test(
        line
      )
    )
    .slice(0, 20)
);

onMounted(async () => {
  if (traceLog.value) {
    traceContent.value = traceLog.value;
    loading.value = false;
    return;
  }

  if (!effectiveBlobId.value) {
    error.value =
      "No trace log ID provided. Return to the results page and try again.";
    loading.value = false;
    return;
  }
  try {
    const content = await fetchTraceLog(effectiveBlobId.value);
    traceContent.value = content;
    redistributionStore.setTraceLog(content);
  } catch (error_) {
    error.value = error_ instanceof Error ? error_.message : String(error_);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <main class="page-main-container overflow-y-auto">
    <div
      class="flex min-h-0 flex-1 flex-col rounded-lg bg-white shadow-[0_2px_8px_rgb(0_0_0/8%)]"
    >
      <div class="panel-card-header shrink-0">
        <h2>📋 Algorithm Trace Log</h2>
        <button
          class="panel-secondary-button"
          @click="
            router.push({
              name: 'results',
              query: effectiveBlobId ? { blobId: effectiveBlobId } : {},
            })
          "
        >
          ← Back to Results
        </button>
      </div>
      <div class="flex min-h-0 flex-1 flex-col gap-4 px-6 py-6">
        <div v-if="loading" class="text-sm text-slate-500">
          Loading trace log…
        </div>
        <div v-else-if="error" class="status-banner-base status-banner-error">
          <span class="text-[20px]">❌</span>
          {{ error }}
        </div>
        <template v-else>
          <div class="grid gap-3 sm:grid-cols-3">
            <div class="rounded border border-slate-200 bg-slate-50 px-4 py-3">
              <p
                class="text-[11px] font-semibold tracking-wide text-slate-500 uppercase"
              >
                Successful Moves
              </p>
              <p class="text-lg font-semibold text-slate-800">
                {{
                  successfulMovesCount !== undefined
                    ? successfulMovesCount.toString()
                    : traceLines.length.toString()
                }}
              </p>
            </div>
            <div class="rounded border border-amber-200 bg-amber-50 px-4 py-3">
              <p
                class="text-[11px] font-semibold tracking-wide text-amber-700 uppercase"
              >
                Warnings
              </p>
              <p class="text-lg font-semibold text-amber-800">
                {{ warningLineCount.toString() }}
              </p>
            </div>
            <div class="rounded border border-rose-200 bg-rose-50 px-4 py-3">
              <p
                class="text-[11px] font-semibold tracking-wide text-rose-700 uppercase"
              >
                Errors
              </p>
              <p class="text-lg font-semibold text-rose-800">
                {{ errorLineCount.toString() }}
              </p>
            </div>
          </div>

          <section class="rounded border border-slate-200 bg-white p-4">
            <h3 class="mb-2 text-sm font-semibold text-slate-800">
              Key events
            </h3>
            <ul
              v-if="keyEvents.length > 0"
              class="m-0 list-disc ps-5 text-sm text-slate-700"
            >
              <li
                v-for="(line, index) in keyEvents"
                :key="`${index.toString()}-${line}`"
              >
                {{ line }}
              </li>
            </ul>
            <p v-else class="text-sm text-slate-500">
              No highlighted events were detected in this trace.
            </p>
          </section>

          <details class="rounded border border-slate-200 bg-slate-50 p-4" open>
            <summary
              class="cursor-pointer text-sm font-semibold text-slate-800"
            >
              Raw trace log
            </summary>
            <pre
              class="mt-3 min-h-0 overflow-auto rounded border border-slate-200 bg-white p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap text-slate-800"
              >{{ displayContent }}</pre
            >
          </details>
        </template>
      </div>
    </div>
  </main>
</template>
